import { useEffect, useMemo, useState } from "react";
import { Link } from "@tanstack/react-router";
import { GitMerge, Search } from "lucide-react";
import { ConfirmDialog } from "@/components/ConfirmDialog";
import { Button } from "@/components/ui/Button";
import {
  DataTable,
  DataTableBody,
  DataTableCell,
  DataTableHead,
  DataTableHeadCell,
  DataTableRow,
  DataTableScroll,
} from "@/components/ui/DataTable";
import {
  LibraryCollection,
  LibraryCollectionTableShell,
} from "@/components/ui/LibraryCollection";
import { useToast } from "@/components/ui/toast";
import { errorMessage } from "@/lib/api";
import {
  useAdminAthletes,
  useAdminMergeAthletes,
} from "@/features/admin/hooks/useAdminData";
import type { Athlete, AthleteDirectoryEntry, AthleteMerge } from "@/lib/types";
import { useAdminUserRows } from "../hooks/useAdminDirectoryLookups";
import {
  AssignmentChip,
  AthletePanelHeading,
  formatIsoDate,
} from "../athleteDisplay";

/** The reason box takes the app's approved field geometry (field surface,
 * 13px radius) like the search field above it; only the focus ring and
 * the resize handle are set here. */
const REASON_CLASS =
  "w-full resize-y border-0 text-ink placeholder:text-ink-muted focus:outline-none focus:ring-1 focus:ring-accent";

const SEARCH_DEBOUNCE_MS = 250;
const MAX_CANDIDATES = 10;

/**
 * Reviewed merge (D10, club admin only — the page gates on the API's
 * `can_merge`): pick another athlete of the club, give a reason, confirm.
 * The other athlete's assignments and linked accounts are repointed onto
 * THIS athlete (the survivor) and the other record is marked `merged`;
 * the server refuses (409) when both hold an assignment on one team.
 */
export function MergeAthleteCard({
  athlete,
  onMerged,
}: {
  athlete: Athlete;
  onMerged: (survivorAthleteId: string) => void;
}) {
  const toast = useToast();
  const [search, setSearch] = useState("");
  const [needle, setNeedle] = useState("");
  useEffect(() => {
    const handle = window.setTimeout(
      () => setNeedle(search.trim()),
      SEARCH_DEBOUNCE_MS,
    );
    return () => window.clearTimeout(handle);
  }, [search]);
  const candidatesQuery = useAdminAthletes(needle, null, {
    enabled: needle.length >= 2,
  });
  const candidates = useMemo(
    () =>
      (needle.length >= 2 ? (candidatesQuery.data ?? []) : [])
        .filter((row) => row.id !== athlete.id && row.status !== "merged")
        .slice(0, MAX_CANDIDATES),
    [candidatesQuery.data, needle, athlete.id],
  );
  const [selected, setSelected] = useState<AthleteDirectoryEntry | null>(null);
  const [reason, setReason] = useState("");
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const mut = useAdminMergeAthletes();
  const canSubmit = !!selected && reason.trim().length > 0 && !mut.isPending;

  const confirm = async () => {
    if (!selected) return;
    setError(null);
    try {
      await mut.mutateAsync({
        survivorAthleteId: athlete.id,
        body: { merged_athlete_id: selected.id, reason: reason.trim() },
      });
      toast.success(`${selected.display_name} merged into ${athlete.display_name}`);
      setConfirmOpen(false);
      setSelected(null);
      setReason("");
      setSearch("");
      onMerged(athlete.id);
    } catch (err) {
      const message = errorMessage(err);
      setError(message);
      toast.error("Couldn't merge the athletes", { description: message });
    }
  };

  return (
    <LibraryCollection
      data-testid="admin-athlete-merge-card"
      aria-labelledby="admin-athlete-merge-title"
      className="admin-match-library-pane admin-library-pane flex flex-col gap-4"
    >
      <AthletePanelHeading
        id="admin-athlete-merge-title"
        eyebrow="Duplicates"
        title="Merge a duplicate into this athlete"
        description="A reviewed action: the other record’s assignments and linked accounts move onto this athlete and it is marked merged. Nothing merges automatically."
      />
      <div className="flex flex-col gap-3">
        <label className="library-search-field">
          <span>Find the duplicate</span>
          <span className="admin-match-search-control library-search-control">
            <Search size={16} aria-hidden="true" />
            <input
              type="search"
              value={search}
              onChange={(e) => {
                setSearch(e.target.value);
                setSelected(null);
              }}
              placeholder="Search the club directory by name"
              data-testid="admin-athlete-merge-search"
            />
          </span>
        </label>
        {needle.length >= 2 ? (
          <div
            data-testid="admin-athlete-merge-candidates"
            className="flex max-h-48 flex-col gap-0.5 overflow-auto rounded-inset bg-elevated p-1"
          >
            {candidatesQuery.isLoading ? (
              <p className="px-2 py-1.5 text-xs text-ink-muted">Searching…</p>
            ) : candidates.length === 0 ? (
              <p className="px-2 py-1.5 text-xs text-ink-muted">
                No other athlete matches.
              </p>
            ) : (
              candidates.map((row) => {
                const isSelected = selected?.id === row.id;
                return (
                  <button
                    key={row.id}
                    type="button"
                    onClick={() => setSelected(isSelected ? null : row)}
                    aria-pressed={isSelected}
                    data-testid="admin-athlete-merge-candidate"
                    data-athlete-id={row.id}
                    className="flex w-full flex-wrap items-center gap-1.5 rounded-inset px-2 py-1.5 text-left text-xs transition-colors hover:bg-field aria-pressed:text-accent"
                  >
                    <span className="text-ink">{row.display_name}</span>
                    {row.birth_year ? (
                      <span className="text-ink-muted">{row.birth_year}</span>
                    ) : null}
                    {(row.assignments ?? []).map((assignment) => (
                      <AssignmentChip
                        key={assignment.player_id}
                        assignment={assignment}
                      />
                    ))}
                    {isSelected ? (
                      <span className="ml-auto text-accent" aria-hidden>
                        ✓
                      </span>
                    ) : null}
                  </button>
                );
              })
            )}
          </div>
        ) : null}
        <label className="library-filter-field">
          <span>
            Reason <strong aria-hidden>*</strong>
          </span>
          <textarea
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            rows={2}
            placeholder="Why these two records are the same athlete"
            className={REASON_CLASS}
            data-testid="admin-athlete-merge-reason"
          />
        </label>
        <div className="flex items-center justify-end">
          <Button
            type="button"
            size="sm"
            disabled={!canSubmit}
            onClick={() => {
              setError(null);
              setConfirmOpen(true);
            }}
            data-testid="admin-athlete-merge"
          >
            <GitMerge aria-hidden />
            Merge into this athlete
          </Button>
        </div>
      </div>

      {confirmOpen && selected ? (
        <ConfirmDialog
          title="Merge athletes"
          danger
          confirmDisabled={mut.isPending}
          message={
            <div className="flex flex-col gap-2 text-xs">
              <p className="text-ink">
                Merge{" "}
                <span className="font-display">{selected.display_name}</span>{" "}
                into{" "}
                <span className="font-display">{athlete.display_name}</span>?
              </p>
              <p className="text-ink-muted">
                The other athlete&rsquo;s assignments and linked accounts move
                onto this one and the other record is marked merged. The merge
                is written to the audit with your name and the reason.
              </p>
              {error ? (
                <p
                  data-testid="admin-athlete-merge-error"
                  className="font-mono text-destructive"
                >
                  {error}
                </p>
              ) : null}
            </div>
          }
          confirmLabel={mut.isPending ? "Merging…" : "Merge"}
          onConfirm={() => void confirm()}
          onClose={() => setConfirmOpen(false)}
          data-testid="admin-athlete-merge-confirm"
        />
      ) : null}
    </LibraryCollection>
  );
}

/** The merge audit (D10): every merge this athlete took part in, survivor
 * and merged record resolved to names where the club directory can (the
 * merged list is the one place a merged athlete is still listed). */
export function MergeAuditList({
  athlete,
  merges,
}: {
  athlete: Athlete;
  merges: AthleteMerge[];
}) {
  const mergedQuery = useAdminAthletes("", "merged", {
    enabled: merges.length > 0,
  });
  const usersQuery = useAdminUserRows(merges.length > 0);
  const mergedNames = useMemo(
    () => new Map((mergedQuery.data ?? []).map((row) => [row.id, row.display_name])),
    [mergedQuery.data],
  );
  const actorNames = useMemo(
    () =>
      new Map(
        (usersQuery.data ?? []).map((user) => [
          user.id,
          user.display_name || user.username,
        ]),
      ),
    [usersQuery.data],
  );
  const nameOf = (athleteId: string) =>
    athleteId === athlete.id
      ? athlete.display_name
      : (mergedNames.get(athleteId) ?? null);

  if (merges.length === 0) return null;
  const rows = [...merges].sort((a, b) =>
    b.created_at.localeCompare(a.created_at),
  );

  return (
    <LibraryCollection
      data-testid="admin-athlete-merge-audit"
      aria-labelledby="admin-athlete-merge-audit-title"
      className="admin-match-library-pane admin-library-pane flex flex-col gap-4"
    >
      <AthletePanelHeading
        id="admin-athlete-merge-audit-title"
        eyebrow="Duplicates"
        title="Merge audit"
      />
      <LibraryCollectionTableShell>
        <DataTableScroll>
          <DataTable className="library-collection-table">
            <colgroup>
              {[16, 34, 18, 32].map((width, index) => (
                <col key={index} style={{ width: `${width}%` }} />
              ))}
            </colgroup>
            <DataTableHead>
              <DataTableRow interactive={false}>
                <DataTableHeadCell>Date</DataTableHeadCell>
                <DataTableHeadCell>Merged record</DataTableHeadCell>
                <DataTableHeadCell>By</DataTableHeadCell>
                <DataTableHeadCell>Reason</DataTableHeadCell>
              </DataTableRow>
            </DataTableHead>
            <DataTableBody>
              {rows.map((merge) => (
                <DataTableRow
                  key={merge.id}
                  data-testid="admin-athlete-merge-audit-row"
                  data-merge-id={merge.id}
                >
                  <DataTableCell>
                    <div className="admin-library-cell">
                      <span>{formatIsoDate(merge.created_at)}</span>
                    </div>
                  </DataTableCell>
                  <DataTableCell>
                    <div className="admin-library-cell">
                      <span>
                        <AuditName
                          id={merge.merged_id}
                          name={nameOf(merge.merged_id)}
                        />
                        {" → "}
                        <AuditName
                          id={merge.survivor_id}
                          name={nameOf(merge.survivor_id)}
                        />
                      </span>
                      {merge.repointed_player_ids?.length ? (
                        <small>
                          {merge.repointed_player_ids.length} assignment
                          {merge.repointed_player_ids.length === 1 ? "" : "s"}{" "}
                          repointed
                        </small>
                      ) : null}
                    </div>
                  </DataTableCell>
                  <DataTableCell>
                    <div className="admin-library-cell">
                      <span>
                        {merge.actor_user_id
                          ? (actorNames.get(merge.actor_user_id) ??
                            merge.actor_user_id)
                          : "unknown"}
                      </span>
                    </div>
                  </DataTableCell>
                  <DataTableCell>
                    <div className="admin-library-cell">
                      <span className="text-ink-muted">
                        {merge.reason || "No reason recorded"}
                      </span>
                    </div>
                  </DataTableCell>
                </DataTableRow>
              ))}
            </DataTableBody>
          </DataTable>
        </DataTableScroll>
      </LibraryCollectionTableShell>
    </LibraryCollection>
  );
}

function AuditName({ id, name }: { id: string; name: string | null }) {
  return (
    <Link
      to="/admin/athletes/$athleteId"
      params={{ athleteId: id }}
      className="font-display text-ink transition-colors hover:text-accent"
    >
      {name ?? id}
    </Link>
  );
}
